import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoryDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (url: string) => {
    setIsOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <section id="categories" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
            Browse Categories
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
            Explore Product Categories
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Select a category from the dropdown below to start discovering premium products curated just for you.
          </p>
        </div>

        <div className="max-w-md mx-auto" ref={dropdownRef}>
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-background-50 border-2 border-background-200 rounded-xl text-foreground-700 hover:border-primary-300 focus:border-primary-400 focus:outline-none transition-all duration-200 cursor-pointer"
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-grid-line text-primary-500 text-lg"></i>
                </span>
                <span className="text-sm font-medium">Select a product category...</span>
              </div>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-foreground-500 text-lg"></i>
              </span>
            </button>

            {isOpen && (
              <div
                className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden z-30 max-h-[400px] overflow-y-auto"
                role="listbox"
              >
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleSelect(cat.url)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-background-100 transition-colors duration-150 cursor-pointer group"
                    role="option"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors duration-150">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-150 block whitespace-nowrap">
                        {cat.name}
                      </span>
                    </div>
                    <span className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <i className="ri-arrow-right-up-line text-primary-500 text-sm"></i>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <p className="text-center text-foreground-500 text-xs mt-4">
            Click any category to open its dedicated product page on our main website
          </p>
        </div>
      </div>
    </section>
  );
}